import { useState, useRef, useEffect } from "react";
import { Maximize2, Minimize2, X, GripHorizontal, RotateCcw } from "lucide-react";

// Footprint (px) reserved by the main menu per variant
const MENU = { left: { l: 208 }, right: { r: 208 }, top: { t: 70 }, dock: { b: 104 } };
const GAP = 8;
const MIN_W = 280, MIN_H = 180, SNAP = 24;

// Reusable floating window: menu-aware positioning + draggable + resizable + fullscreen.
// Position and size are persisted per `id` in localStorage. Children fill it with `absolute inset-0`.
export default function FloatingPanel({ id, menuVariant = "left", anchor = "left", width = "full", zIndex = 850, onClose, children }) {
  const key = `fp_panel_${id}`;
  const [st, setSt] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; }
  });
  const [vp, setVp] = useState({ w: typeof window !== "undefined" ? window.innerWidth : 1280, h: typeof window !== "undefined" ? window.innerHeight : 800 });
  const gesture = useRef(null);
  const listeners = useRef(null);

  useEffect(() => {
    const onResize = () => setVp({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const save = (v) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore */ } };
  const patch = (p) => setSt((s) => { const v = { ...s, ...p }; save(v); return v; });
  const toggleFs = () => patch({ fs: !st.fs });
  const restore = () => { setSt({}); try { localStorage.removeItem(key); } catch { /* ignore */ } };

  // Geometry
  const m = MENU[menuVariant] || {};
  const mob = vp.w < 768;
  const leftIn = (mob ? 0 : (m.l || 0)) + GAP, rightIn = (mob ? 0 : (m.r || 0)) + GAP, topIn = (m.t || 0) + GAP, bottomIn = (m.b || 0) + GAP;
  const availW = Math.max(MIN_W, vp.w - leftIn - rightIn);
  const availH = Math.max(MIN_H, vp.h - topIn - bottomIn);
  const defW = width === "full" ? availW : Math.min(width, availW);
  const defH = availH;
  const defLeft = (anchor === "right" && width !== "full") ? (vp.w - rightIn - defW) : leftIn;
  const defTop = topIn;

  const w = Math.min(st.w ?? defW, vp.w - 16);
  const h = Math.min(st.h ?? defH, vp.h - 16);
  const left = st.left ?? defLeft;
  const top = st.top ?? defTop;
  const fs = !!st.fs;

  const endGesture = () => {
    const g = gesture.current;
    if (listeners.current) {
      window.removeEventListener("pointermove", listeners.current.move);
      window.removeEventListener("pointerup", listeners.current.up);
      listeners.current = null;
    }
    gesture.current = null;
    if (g && g.type === "move") {
      // Snap to menu-aware container edges
      setSt((s) => {
        let nl = s.left ?? left, nt = s.top ?? top;
        const rightEdge = vp.w - rightIn, bottomEdge = vp.h - bottomIn;
        if (Math.abs(nl - leftIn) < SNAP) nl = leftIn;
        else if (Math.abs((nl + w) - rightEdge) < SNAP) nl = rightEdge - w;
        if (Math.abs(nt - topIn) < SNAP) nt = topIn;
        else if (Math.abs((nt + h) - bottomEdge) < SNAP) nt = bottomEdge - h;
        const v = { ...s, left: nl, top: nt }; save(v); return v;
      });
    } else {
      setSt((s) => { save(s); return s; });
    }
  };

  const startDrag = (e) => {
    if (fs) return;
    e.preventDefault(); e.stopPropagation();
    gesture.current = { type: "move", sx: e.clientX, sy: e.clientY, left, top };
    const move = (ev) => {
      const g = gesture.current; if (!g) return;
      setSt((s) => ({ ...s, left: g.left + (ev.clientX - g.sx), top: g.top + (ev.clientY - g.sy) }));
    };
    listeners.current = { move, up: endGesture };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", endGesture);
  };

  const startResize = (e) => {
    if (fs) return;
    e.preventDefault(); e.stopPropagation();
    gesture.current = { type: "resize", sx: e.clientX, sy: e.clientY, w, h };
    const move = (ev) => {
      const g = gesture.current; if (!g) return;
      setSt((s) => ({ ...s, w: Math.max(MIN_W, g.w + (ev.clientX - g.sx)), h: Math.max(MIN_H, g.h + (ev.clientY - g.sy)) }));
    };
    listeners.current = { move, up: endGesture };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", endGesture);
  };

  useEffect(() => () => {
    if (listeners.current) {
      window.removeEventListener("pointermove", listeners.current.move);
      window.removeEventListener("pointerup", listeners.current.up);
    }
  }, []);

  // Global fullscreen toggle (e.g. keyboard shortcut "F")
  useEffect(() => {
    const onFs = () => setSt((s) => { const v = { ...s, fs: !s.fs }; save(v); return v; });
    window.addEventListener("fp-fullscreen-toggle", onFs);
    return () => window.removeEventListener("fp-fullscreen-toggle", onFs);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const style = fs
    ? { top: 0, left: 0, right: 0, bottom: 0, zIndex: 2000 }
    : { left, top, width: w, height: h, zIndex };

  const moved = st.left != null || st.top != null || st.w != null || st.h != null || fs;

  return (
    <div className="absolute overflow-hidden rounded-2xl border border-cyan-500/20 shadow-2xl shadow-black/60 ring-1 ring-black/40 kv-anim-in" style={style} data-testid={`floating-${id}`}>
      {children}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-0.5 rounded-b-lg bg-slate-900/90 backdrop-blur-xl border border-t-0 border-cyan-500/25 px-1.5 py-0.5 shadow-lg shadow-cyan-950/30 select-none"
        onDoubleClick={toggleFs} data-testid={`floating-bar-${id}`}>
        <button title="Przesuń (przeciągnij)" onPointerDown={startDrag} data-testid={`floating-drag-${id}`}
          className="cursor-move text-slate-300 hover:text-white px-1 touch-none"><GripHorizontal size={16} /></button>
        {moved && !fs && (
          <button title="Przywróć domyślne położenie i rozmiar" onClick={restore} data-testid={`floating-reset-${id}`}
            className="text-slate-300 hover:text-cyan-300 px-1"><RotateCcw size={14} /></button>
        )}
        <button title="Pełny ekran" onClick={toggleFs} data-testid={`floating-fs-${id}`}
          className="text-slate-300 hover:text-cyan-300 px-1">{fs ? <Minimize2 size={15} /> : <Maximize2 size={15} />}</button>
        {onClose && <button title="Zamknij" onClick={onClose} data-testid={`floating-close-${id}`}
          className="text-slate-300 hover:text-red-400 px-1"><X size={16} /></button>}
      </div>
      {!fs && (
        <div onPointerDown={startResize} data-testid={`floating-resize-${id}`} title="Zmień rozmiar"
          className="absolute bottom-0 right-0 z-[60] w-4 h-4 cursor-nwse-resize touch-none"
          style={{ background: "linear-gradient(135deg, transparent 50%, rgb(148 163 184) 50%, rgb(148 163 184) 60%, transparent 60%, transparent 72%, rgb(148 163 184) 72%, rgb(148 163 184) 82%, transparent 82%)" }} />
      )}
    </div>
  );
}
